// src/pages/Dashboard.js
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Rating,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  RestaurantMenu as RestaurantMenuIcon,
  FitnessCenter as FitnessCenterIcon,
  SupervisedUserCircleOutlined,
  StarBorder as StarBorderIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";

import { AuthContext } from "../../context/AuthContext";
import { DatosNutriologoContext } from "../../context/DatosNutriologoContext";

import RoutineTabs from "./rutinas/RoutineTabs";
import DietSection from "./miDieta/DietSection";
import DatosPaciente from "./misDatos/DatosPaciente";
import EvaluacionesService from "../../services/EvaluacionesService";

import logo1 from "../../assets/images/logo1.png";

const drawerWidth = 300;
const formatPhoneForWhatsApp = (phone = "") => phone.replace(/\D/g, "");
const ensureHttpUrl = (url = "") =>
  url ? (/^https?:\/\//i.test(url) ? url : `https://${url}`) : "";

function Dashboard(props) {
  /* ===================== Contextos ===================== */
  const { obtenerDatosNutriologoPorSD, datosNutriologo } = useContext(
    DatosNutriologoContext
  );
  const { logout, user } = useContext(AuthContext);

  /* ===================== Efectos ===================== */
  useEffect(() => {
    obtenerDatosNutriologoPorSD();
  }, []);

  /* ===================== Datos del nutriólogo ===================== */
  const nutriologoInfo = datosNutriologo?.nutriologoInfo || {};
  const nombreAMostrar = nutriologoInfo.nombreAMostrar?.trim() || "";
  const nombreUsuario = nutriologoInfo?.usuario?.nombre || "";
  const apellidosUsuario = nutriologoInfo?.usuario?.apellidos || "";
  const nutriologoNombre =
    nombreAMostrar || `${nombreUsuario} ${apellidosUsuario}`.trim();
  const nutriologoInitials =
    nutriologoNombre
      ?.split(" ")
      .map((word) => word?.charAt(0) || "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "NU";
  const nutriologoImage =
    nutriologoInfo?.usuario?.imagen &&
    nutriologoInfo.usuario.imagen !== "default"
      ? nutriologoInfo.usuario.imagen
      : "";
  const direccionAMostrar = nutriologoInfo.direccionAMostrar?.trim() || "";
  const telefonoAMostrar = nutriologoInfo.telefonoAMostrar?.trim() || "";
  const urlFacebook = nutriologoInfo.urlFacebook?.trim() || "";
  const facebookLink = ensureHttpUrl(urlFacebook);
  const facebookLabel =
    nutriologoInfo.nombreFacebook?.trim() || direccionAMostrar || "Facebook";
  const hasWhatsApp = Boolean(telefonoAMostrar);
  const hasFacebook = Boolean(facebookLink);
  const whatsappLink = hasWhatsApp
    ? `https://wa.me/${formatPhoneForWhatsApp(telefonoAMostrar)}`
    : "";

  /* ===================== Hooks de UI ===================== */
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { window: containerWindow } = props;
  const container =
    containerWindow !== undefined
      ? () => containerWindow().document.body
      : undefined;

  /* ===================== Estados ===================== */
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dietasAsignadas, setDietasAsignadas] = useState([]);
  const [pacienteId, setPacienteId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("Mi Dieta");
  const [selectedDay, setSelectedDay] = useState("lunes");
  const [showSuperDialog, setShowSuperDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingValue, setRatingValue] = useState(5);
  const [commentValue, setCommentValue] = useState("");
  const [sendingReview, setSendingReview] = useState(false);
  const [reviewInfo, setReviewInfo] = useState({
    loaded: false,
    hasReview: false,
    calificacion: null,
    comentario: "",
  });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const reviewInfoRef = useRef(reviewInfo);

  useEffect(() => {
    reviewInfoRef.current = reviewInfo;
  }, [reviewInfo]);

  /* ===================== Handlers UI ===================== */
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleOpenSuperList = () => setShowSuperDialog(true);
  const handleCloseSuperList = () => setShowSuperDialog(false);
  const handleLogoutClick = () => {
    handleMoreClose();
    Swal.fire({
      title: "¿Cerrar sesión?",
      text: "Confirma que deseas salir de Nutriolab Paciente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Cerrar sesión",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
      }
    });
  };
  const handleMissingContactInfo = (fieldLabel) => {
    Swal.fire({
      icon: "info",
      title: "Información no disponible",
      text: `Tu nutriólogo aún no ha proporcionado ${fieldLabel}.`,
      confirmButtonColor: "#0ea5e9",
    });
  };

  const primaryColor = nutriologoInfo.color1 || theme.palette.primary.main;
  const secondaryColor = nutriologoInfo.color2 || theme.palette.primary.dark;
  const drawerAccent = nutriologoInfo.color3 || theme.palette.secondary.main;
  const drawerTextColor = "#0f172a";
  const drawerMutedText = "rgba(15,23,42,0.65)";

  const primaryGradient = useMemo(
    () => `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    [primaryColor, secondaryColor]
  );
  const reviewHelperText = reviewInfo.hasReview
    ? "Puedes actualizar la evaluación que ya enviaste."
    : "Comparte tu experiencia con tu asesor.";

  const patientName = `${pacienteId.nombre || ""} ${
    pacienteId.apellidos || ""
  }`.trim();
  const heroGradient = primaryGradient;
  const appBarBackground = primaryGradient;
  const menuItems = [
    {
      key: "Mi Dieta",
      title: "Mi dieta",
      description: "Plan diario, lista inteligente y macros",
      icon: <RestaurantMenuIcon />,
    },
    {
      key: "Mi Rutina",
      title: "Mi rutina",
      description: "Entrenamientos y progresión semanal",
      icon: <FitnessCenterIcon />,
    },
    {
      key: "Mis Datos",
      title: "Mis datos",
      description: "Información personal y progreso",
      icon: <SupervisedUserCircleOutlined />,
    },
  ];

  /* ===================== Fetch dietas (con idNutriologo) ===================== */
  const fetchDietasAsignadas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const telefono = user?.telefono;
      const password = user?.password;
      const idNutriologo =
        user?.paciente?.nutriologo || user?.nutriologo || null;

      if (!telefono || !password || !idNutriologo) {
        throw new Error("Datos de usuario incompletos.");
      }

      const payload = { telefono, password, idNutriologo };

      const res = await fetch("/api/paciente/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "No pudimos cargar tus planes.");
      }

      const info = data.data;
      const paciente = info?.paciente;

      if (!paciente) {
        throw new Error("La respuesta no incluye información del paciente.");
      }

      setPacienteId(paciente);
      setDietasAsignadas(paciente.dietasAsignadas || []);
    } catch (err) {
      console.error("Error al cargar dietas asignadas", err);
      setError(err.message || "Ocurrió un error inesperado.");
      setDietasAsignadas([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.telefono && user?.password) {
      fetchDietasAsignadas();
    }
  }, [fetchDietasAsignadas, user]);

  const loadExistingReview = useCallback(async () => {
    try {
      setReviewLoading(true);
      const response = await EvaluacionesService.fetchMyReview();
      const payload = response?.data || response || {};
      const info = {
        loaded: true,
        hasReview: Boolean(payload?.yaCalificado),
        calificacion: payload?.calificacion ?? null,
        comentario: payload?.comentario ?? "",
      };
      setReviewInfo(info);
      return info;
    } catch (error) {
      if (error?.response?.status === 404) {
        const emptyState = {
          loaded: true,
          hasReview: false,
          calificacion: null,
          comentario: "",
        };
        setReviewInfo(emptyState);
        return emptyState;
      }
      console.error("No se pudo obtener tu evaluación", error);
      const fallback = { ...reviewInfoRef.current, loaded: true };
      setReviewInfo(fallback);
      return fallback;
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExistingReview();
  }, [loadExistingReview]);

  const handleMoreClick = (event) => setMoreAnchorEl(event.currentTarget);
  const handleMoreClose = () => setMoreAnchorEl(null);

  const prepareRatingDialog = useCallback(async () => {
    const info = reviewInfoRef.current.loaded
      ? reviewInfoRef.current
      : await loadExistingReview();
    const baseRating =
      info.hasReview && info.calificacion ? info.calificacion : 5;
    setRatingValue(baseRating);
    setCommentValue(info.hasReview ? info.comentario || "" : "");
    setRatingDialogOpen(true);
  }, [loadExistingReview]);

  const handleEvaluateClick = () => {
    handleMoreClose();
    prepareRatingDialog().catch(() =>
      setSnackbar({
        open: true,
        severity: "error",
        message: "No pudimos preparar tu evaluación. Intenta nuevamente.",
      })
    );
  };

  const handleCloseRatingDialog = () => {
    if (sendingReview) return;
    setRatingDialogOpen(false);
  };

  const handleSubmitReview = async () => {
    if (ratingValue < 1) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Selecciona al menos una estrella antes de enviar.",
      });
      return;
    }
    setSendingReview(true);
    const alreadyReviewed = reviewInfoRef.current.hasReview;
    try {
      const action = alreadyReviewed
        ? EvaluacionesService.updateReview
        : EvaluacionesService.submitReview;
      await action({ calificacion: ratingValue, comentario: commentValue });
      await loadExistingReview();
      setSnackbar({
        open: true,
        severity: "success",
        message: alreadyReviewed
          ? "Actualizaste tu evaluación con éxito."
          : "¡Gracias! Tu evaluación fue enviada.",
      });
      setRatingDialogOpen(false);
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      setSnackbar({
        open: true,
        severity: "error",
        message: serverMessage || "No pudimos guardar tu evaluación.",
      });
    } finally {
      setSendingReview(false);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        color: drawerTextColor,
        borderRight: "1px solid rgba(15,23,42,0.08)",
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid rgba(15,23,42,0.06)",
          background: "linear-gradient(180deg,#fff,#f8fafc)",
        }}
      >
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3.5,
              background: primaryGradient,
              color: "#fff",
              textAlign: "center",
              boxShadow: "0 25px 55px rgba(15,23,42,0.35)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 2,
                fontWeight: 700,
                opacity: 0.85,
              }}
            >
              Responsable
            </Typography>
            <Avatar
              src={nutriologoImage || undefined}
              alt={nutriologoNombre || "Nutriólogo"}
              sx={{
                width: 76,
                height: 76,
                mx: "auto",
                my: 2,
                border: "3px solid rgba(255,255,255,0.6)",
                boxShadow: "0 15px 35px rgba(15,23,42,0.25)",
                background: nutriologoImage ? "#fff" : "rgba(255,255,255,0.2)",
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              {nutriologoInitials}
            </Avatar>
            <Typography variant="h6" fontWeight={800}>
              {nutriologoNombre || "Nutriólogo"}
            </Typography>
          </Paper>
        </Stack>
      </Box>

      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: drawerMutedText, letterSpacing: 2, fontWeight: 600 }}
        >
          Panel principal
        </Typography>
        <List sx={{ mt: 2 }} disablePadding>
          {menuItems.map((item) => {
            const selected = selectedMenu === item.key;
            return (
              <ListItem key={item.key} disablePadding sx={{ mb: 1.5 }}>
                <ListItemButton
                  onClick={() => {
                    setSelectedMenu(item.key);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }}
                  selected={selected}
                  sx={{
                    borderRadius: 3,
                    px: 2.5,
                    py: 1.5,
                    alignItems: "flex-start",
                    gap: 1.5,
                    backgroundColor: selected
                      ? "rgba(79,70,229,0.08)"
                      : "transparent",
                    border: selected
                      ? `1px solid ${drawerAccent}`
                      : "1px solid rgba(15,23,42,0.04)",
                    boxShadow: selected
                      ? "0 20px 45px rgba(79,70,229,0.15)"
                      : "none",
                    transition: "all 0.25s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      color: selected ? primaryColor : drawerMutedText,
                      mt: 0.2,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontWeight: 700,
                      color: selected ? primaryColor : drawerTextColor,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleMoreClick}
              selected={moreMenuOpen}
              sx={{
                borderRadius: 3,
                px: 2.5,
                py: 1.5,
                alignItems: "flex-start",
                gap: 1.5,
                border: moreMenuOpen
                  ? `1px solid ${drawerAccent}`
                  : "1px solid rgba(15,23,42,0.04)",
                boxShadow: moreMenuOpen
                  ? "0 20px 45px rgba(79,70,229,0.15)"
                  : "none",
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  color: moreMenuOpen ? primaryColor : drawerMutedText,
                  mt: 0.2,
                  transition: "transform 0.2s ease",
                  transform: moreMenuOpen ? "rotate(180deg)" : "none",
                }}
              >
                <KeyboardArrowDownIcon />
              </ListItemIcon>
              <ListItemText
                primary="Más"
                primaryTypographyProps={{
                  fontWeight: 700,
                  color: moreMenuOpen ? primaryColor : drawerTextColor,
                }}
                secondaryTypographyProps={{
                  color: drawerMutedText,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Box
        sx={{
          pt: 3,
          pb: 3,
          px: 0,
          borderTop: "1px solid rgba(15,23,42,0.06)",
          background: "linear-gradient(180deg,#f8fafc,#f1f5f9)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            background: "#fff",
            border: "1px solid rgba(15,23,42,0.05)",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Contacto
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 1.5 }}>
            {hasWhatsApp && whatsappLink ? (
              <MuiLink
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "inherit",
                }}
              >
                <WhatsAppIcon sx={{ color: "#22c55e" }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {telefonoAMostrar}
                </Typography>
              </MuiLink>
            ) : (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                role="button"
                tabIndex={0}
                onClick={() =>
                  handleMissingContactInfo("su número de WhatsApp")
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleMissingContactInfo("su número de WhatsApp");
                  }
                }}
                sx={{
                  cursor: "pointer",
                  color: "text.secondary",
                  px: 1,
                  py: 0.75,
                  borderRadius: 2,
                  border: "1px dashed rgba(15,23,42,0.1)",
                }}
              >
                <WhatsAppIcon sx={{ color: "#22c55e" }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  WhatsApp no disponible
                </Typography>
              </Stack>
            )}

            {hasFacebook ? (
              <MuiLink
                href={facebookLink}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "inherit",
                }}
              >
                <FacebookIcon sx={{ color: "#1877f2" }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {facebookLabel}
                </Typography>
              </MuiLink>
            ) : (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                role="button"
                tabIndex={0}
                onClick={() =>
                  handleMissingContactInfo("su enlace de Facebook")
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleMissingContactInfo("su enlace de Facebook");
                  }
                }}
                sx={{
                  cursor: "pointer",
                  color: "text.secondary",
                  px: 1,
                  py: 0.75,
                  borderRadius: 2,
                  border: "1px dashed rgba(15,23,42,0.1)",
                }}
              >
                <FacebookIcon sx={{ color: "#1877f2" }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Facebook no disponible
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );

  /* ===================== Render de página ===================== */
  const renderContent = () => {
    switch (selectedMenu) {
      case "Mi Dieta":
        return (
          <DietSection
            dietasAsignadas={dietasAsignadas}
            loading={loading}
            error={error}
            fetchDietasAsignadas={fetchDietasAsignadas}
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
            tabValue={tabValue}
            setTabValue={setTabValue}
            showSuperDialog={showSuperDialog}
            onOpenSuperList={handleOpenSuperList}
            onCloseSuperList={handleCloseSuperList}
            primaryGradient={primaryGradient}
            nutriologoNombre={nutriologoNombre}
            direccionAMostrar={direccionAMostrar}
            telefonoAMostrar={telefonoAMostrar}
            color3={nutriologoInfo.color3}
          />
        );
      case "Mi Rutina":
        return (
          <RoutineTabs
            rutinasAsignadas={pacienteId.rutinasAsignadas || []}
            color1={nutriologoInfo.color1}
            color2={nutriologoInfo.color2}
            color3={nutriologoInfo.color3}
          />
        );
      case "Mis Datos":
        return <DatosPaciente />;
      default:
        return (
          <Typography sx={{ mt: 4 }} align="center">
            Selecciona una opción del menú.
          </Typography>
        );
    }
  };

  /* ===================== Layout general ===================== */
  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        overflow: "hidden",
        bgcolor: "#f8f9fc",
      }}
    >
      <CssBaseline />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(248,250,252,0.8)",
          backdropFilter: "blur(12px)",
          zIndex: -1,
        }}
      />

      <AppBar
        position="fixed"
        sx={{
          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, sm: `${drawerWidth}px` },
          background: appBarBackground,
          boxShadow: "0 8px 24px rgba(15,23,42,0.25)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Toolbar sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexGrow: 1 }}
          >
            <Box component="img" src={logo1} alt="Logo" sx={{ height: 30 }} />
            <Box sx={{ color: "#f8fafc" }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                {nutriologoNombre}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1 }}>
                {menuItems.find((item) => item.key === selectedMenu)?.title ||
                  "Dashboard"}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={patientName || "Paciente"}
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(248,250,252,0.4)",
              color: "#fff",
              fontWeight: 700,
              background: "rgba(248,250,252,0.1)",
            }}
          />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        <Stack spacing={3}>
          <Box
            sx={{
              background: heroGradient,
              borderRadius: { xs: 3, md: 4 },
              color: "#fff",
              p: { xs: 2.5, md: 3.5 },
              boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Programa personalizado · Programa nutricional
                </Typography>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={800}
                  sx={{ lineHeight: 1.2 }}
                >
                  Hola {patientName || "Paciente"}, este es tu espacio saludable
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Diseñado por {nutriologoNombre || "tu nutriólogo"}
                </Typography>
              </Stack>
              <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {menuItems.find((item) => item.key === selectedMenu)?.title ||
                    "Dashboard"}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderContent()
          )}
        </Stack>
      </Box>
      <Menu
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={handleEvaluateClick}
          disabled={reviewLoading && !reviewInfo.loaded}
          sx={{ gap: 1.5, alignItems: "flex-start", maxWidth: 360 }}
        >
          <StarBorderIcon fontSize="small" color="warning" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" fontWeight={700}>
              Evaluar a mi asesor
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {reviewHelperText}
            </Typography>
          </Box>
          {reviewLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </MenuItem>
        <MenuItem
          onClick={handleLogoutClick}
          sx={{
            gap: 1.5,
            alignItems: "center",
            color: "#ef4444",
            fontWeight: 700,
          }}
        >
          <LogoutIcon fontSize="small" sx={{ color: "#ef4444" }} />
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: "#ef4444" }}
          >
            Cerrar sesión
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={ratingDialogOpen}
        onClose={handleCloseRatingDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            background: primaryGradient,
            color: "#fff",
            fontWeight: 800,
            pb: 2,
          }}
        >
          Califica a tu nutricionista
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tu opinión nos ayuda a mejorar la experiencia de otros pacientes.
          </Typography>
          <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Rating
              name="advisor-rating"
              value={ratingValue}
              precision={1}
              size="large"
              onChange={(_, value) => setRatingValue(value || 0)}
              sx={{
                "& .MuiRating-iconFilled": { color: "#FFD60A" },
                "& .MuiRating-iconHover": { color: "#FFD60A" },
              }}
            />
            <Typography fontWeight={700}>
              {ratingValue > 0
                ? `${ratingValue} ${
                    ratingValue === 1 ? "estrella" : "estrellas"
                  }`
                : "Selecciona una calificación"}
            </Typography>
          </Stack>

          <TextField
            label="Cuéntanos tu experiencia"
            multiline
            minRows={3}
            fullWidth
            value={commentValue}
            onChange={(event) => setCommentValue(event.target.value)}
            placeholder="Excelente atención, puntual y amable..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseRatingDialog} disabled={sendingReview}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={sendingReview || ratingValue < 1}
          >
            {sendingReview ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
